import React from 'react';
import { Refresh } from 'Components/DesignSystem/Iconography/Icons';
import Button from 'Components/DesignSystem/Button/Button';
import { ControlSize } from 'types/DesignSystem/controlSize';

type ReloadButtonProps = {
    loading: boolean;
    onClick: () => void;
    /**
     * What is being reloaded. Becomes the accessible name — an icon-only control has
     * no text, so without this the button announces nothing at all. Defaults to the
     * generic verb rather than being required, so the three existing call sites keep
     * compiling; each should pass something specific.
     */
    label?: string;
    /**
     * § 265 — which rung of the control grid (`Components/DesignSystem/controlSize.ts`:
     * sm 28 / md 36 / lg 44) this square sits on.
     *
     * It defaults to `sm` so the three existing call sites render exactly what they
     * rendered before the prop existed. The Compatibility page header passes `md`,
     * because a toolbar is one object only when its controls share an edge and the
     * other four controls in that row are all 36px — a 28px square beside them is
     * centred against everything and aligned with nothing.
     */
    size?: ControlSize;
}

/**
 * The icon-only refresh control (§ 108).
 *
 * **This component was completely unstyled in production and had been for its whole
 * life.** Its className was
 *
 *   `w-8 h-8 py-2 px-2 rounded border border-[#091caa] flex justify-center items-center`
 *
 * — nine utilities, not one of them carrying the `tw-` prefix this project configures
 * (`tailwind.config.js`: `prefix: 'tw-'`). Tailwind therefore generated **none** of
 * them: no size, no padding, no radius, no border, no centring. The rendered control
 * was a bare 20px glyph with the browser's default button chrome, which is why it
 * never looked like the bordered square the classes describe. The only class that ever
 * applied was the `bg-gray-200`/`bg-white` pair in the template literal — also
 * unprefixed, also dead. `tsc` cannot see this, and no test asserted geometry, so it
 * shipped silently.
 *
 * Rather than re-prefix nine hand-rolled utilities, the control becomes the thing they
 * were describing: a `secondary` DS Button at `sm`, which is a 32px bordered square
 * with centred content and the design system's own focus ring and disabled treatment.
 *
 * The second fix is the accessible name. An icon-only button with no text and no
 * `aria-label` announces as "button" and nothing more; both icons are decorative SVGs.
 * The name now also states the in-progress condition, because `disabled` alone tells a
 * screen-reader user the control is unavailable but not why.
 *
 * `data-testid` is unchanged — `components-reloadbutton-button-1` — so every existing
 * spec and the three call sites (`RuleHeader`, `ARS/index`, `RuleView`) keep working.
 */
const ReloadButton = ({ loading, onClick, label = 'Refresh', size = 'sm' }: ReloadButtonProps) => {

    return (
        <Button
            variant="secondary"
            size={size}
            aria-label={loading ? `${label} (in progress)` : label}
            data-testid="components-reloadbutton-button-1"
            disabled={loading}
            onClick={onClick}
        >
            {/* DS Refresh follows the button's ink via currentColor. The old pair
                (GmsIcons ReloadMediumIcon / SMIL-animated RotatingReloadIcon) baked
                stroke #091CAA — near-invisible on a dark ground. Spin is the same
                tw-animate-spin the Rules Log drawer uses. */}
            <Refresh className={loading ? 'tw-animate-spin' : undefined} />
        </Button>
    );
}

export default ReloadButton;
